import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useCart } from "../../contexts/CartContext";
import { Footer } from "../common";
import "../../styles/CartScreen.css";
import {
  getCart,
  updateCartItem,
  removeCartItem,
  removeSelected,
} from "../../services/cart";
import { getProductById } from "../../services/products";
import { getAuthMode } from "../../utils/authHelpers";
import { toast } from "react-toastify";

/* ── Helpers ──────────────────────────────────────────── */
const toHttps = (u) =>
  typeof u === "string" ? u.replace("http://", "https://") : u;

const parseList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    if (value.length === 0) return [];
    if (
      value.length === 1 &&
      typeof value[0] === "string" &&
      value[0].includes(",")
    )
      return value[0]
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    return value.map((v) => String(v).trim()).filter(Boolean);
  }
  if (typeof value === "string")
    return value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  return [];
};

/* ── Price resolver — works for both user & guest response shapes ── */
const resolveProductPrice = (product) => {
  const final = Number(product?.final_price ?? 0);
  const flash = Number(product?.flash_sale_price ?? 0);
  const discount = Number(product?.product_discount_price ?? 0);
  const regular = Number(product?.price ?? 0);
  if (final > 0) return final;
  if (flash > 0) return flash;
  if (discount > 0) return discount;
  return regular;
};

/*
 * mapLines — normalises both shapes into the same cart item object:
 *
 *  User shape   : { id, product: {...}, color, size, quantity, subtotal }
 *  Guest shape  : { id, cart_item_id, product: {...}, color, size, quantity, subtotal }
 *
 * The guest response wraps items inside data.data (array) exactly like the
 * user response, so no separate branch is needed here.
 */
const mapLines = (lines) =>
  lines.map((line) => {
    const qty = Number(line.quantity ?? 1);
    const unitPrice = resolveProductPrice(line.product);
    const originalPrice = Number(
      line.product?.original_price ?? line.product?.price ?? 0,
    );
    const isFlashSale = !!line.product?.is_flash_sale;
    const subtotal = Number(line.subtotal ?? unitPrice * qty);

    return {
      // guest uses uuid strings, user uses numeric ids — both work as keys
      id: line.id ?? line.cart_item_id,
      productId: line.product?.id,
      name: line.product?.name ?? "Product",
      brand: line.product?.brand ?? "",
      vendorId: line.product?.vendor?.id ?? null,
      storeName: line.product?.vendor?.store_name ?? "Store",
      store_name: line.product?.vendor?.store_name ?? "Store",
      unique_name: line.product?.vendor?.unique_name ?? null,
      color: line.color ?? "",
      size: line.size ?? "",
      unitPrice,
      price: unitPrice,
      originalPrice,
      isFlashSale,
      subtotal,
      quantity: qty,
      weight: Number(line.product?.weight ?? 0),
      weight_unit: line.product?.weight_unit ?? "kg",
      image: toHttps(line.product?.product_images?.[0] ?? ""),
      selected: false,
      availableColors: parseList(
        line.product?.color ??
          line.product?.colors ??
          line.product?.attributes?.color ??
          null,
      ),
      availableSizes: parseList(
        line.product?.size ??
          line.product?.sizes ??
          line.product?.attributes?.size ??
          null,
      ),
    };
  });

/* ── Icons ────────────────────────────────────────────── */
const CartIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);
const TrashIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const EditIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const MinusIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <path d="M5 12H19" />
  </svg>
);
const PlusIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <path d="M12 5V19M5 12H19" />
  </svg>
);
const BagIcon = () => (
  <svg
    width="30"
    height="30"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 0 1-8 0" />
  </svg>
);
const ReceiptIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

/* ═══════════════════════════════════════════════════════
   CartScreen
═══════════════════════════════════════════════════════ */
const CartScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { updateCartCount } = useCart();

  const [loading, setLoading] = useState(true);
  const [selectAll, setSelectAll] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [editColor, setEditColor] = useState("");
  const [editSize, setEditSize] = useState("");
  const [updating, setUpdating] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);

  /* ── Load cart ── */
  const loadCart = async () => {
    setLoading(true);
    try {
      const { data } = await getCart();

      /*
       * Both user and guest APIs return:
       *   { data: { data: [...items] } }   ← paginated / wrapped
       * or occasionally:
       *   { data: [...items] }             ← flat array
       */
      const serverItems = Array.isArray(data?.data?.data)
        ? data.data.data
        : Array.isArray(data?.data)
          ? data.data
          : [];

      setCartItems(mapLines(serverItems));
    } catch {
      toast.error("Could not load the cart. Please refresh.");
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCart();
  }, []);

  /* ── Group by vendor ── */
  const groupedItems = useMemo(() => {
    const map = {};
    cartItems.forEach((item) => {
      const key = String(item.vendorId ?? "default");
      if (!map[key]) {
        map[key] = {
          storeName: item.storeName,
          vendorId: item.vendorId,
          unique_name: item.unique_name,
          latestId: item.id,
          items: [],
        };
      }
      map[key].items.push(item);
      // uuid strings don't compare with > so we just keep first-encountered order
      if (typeof item.id === "number" && item.id > map[key].latestId)
        map[key].latestId = item.id;
    });
    return map;
  }, [cartItems]);

  const selectedItems = cartItems.filter((i) => i.selected);
  const totalItems = selectedItems.length;
  const totalAmount = selectedItems.reduce(
    (s, i) => s + Number(i.subtotal ?? i.unitPrice * i.quantity),
    0,
  );

  /* ── Selection ── */
  const handleSelectAll = () => {
    const val = !selectAll;
    setSelectAll(val);
    setCartItems((items) => items.map((i) => ({ ...i, selected: val })));
  };

  const handleItemSelect = (id) =>
    setCartItems((items) =>
      items.map((i) => (i.id === id ? { ...i, selected: !i.selected } : i)),
    );

  const handleStoreSelect = (vendorKey) => {
    const store = groupedItems[vendorKey];
    if (!store) return;
    const allSelected = store.items.every((i) => i.selected);
    setCartItems((items) =>
      items.map((i) =>
        String(i.vendorId ?? "default") === vendorKey
          ? { ...i, selected: !allSelected }
          : i,
      ),
    );
  };

  useEffect(() => {
    setSelectAll(cartItems.length > 0 && cartItems.every((i) => i.selected));
  }, [cartItems]);

  /* ── Quantity ── */
  const handleQuantityChange = async (itemId, change) => {
    const item = cartItems.find((i) => i.id === itemId);
    if (!item) return;
    const oldQty = item.quantity;
    const newQty = Math.max(1, oldQty + change);
    setCartItems((items) =>
      items.map((i) =>
        i.id === itemId
          ? { ...i, quantity: newQty, subtotal: i.unitPrice * newQty }
          : i,
      ),
    );
    try {
      await updateCartItem({ lineId: itemId, quantity: newQty });
      updateCartCount();
    } catch {
      toast.error("Failed to update quantity.");
      setCartItems((items) =>
        items.map((i) =>
          i.id === itemId
            ? { ...i, quantity: oldQty, subtotal: i.unitPrice * oldQty }
            : i,
        ),
      );
    }
  };

  /* ── Edit modal ── */
  const handleEditItem = async (item) => {
    setEditingItem(item);
    setEditColor(item.color || "");
    setEditSize(item.size || "");
    const hasColors =
      Array.isArray(item.availableColors) && item.availableColors.length > 0;
    const hasSizes =
      Array.isArray(item.availableSizes) && item.availableSizes.length > 0;
    if (!item.productId || (hasColors && hasSizes)) return;
    try {
      setOptionsLoading(true);
      const { data } = await getProductById(item.productId);
      const p = data?.data || data;
      const colorsFromApi = parseList(
        p?.color ?? p?.colors ?? p?.attributes?.color ?? null,
      );
      const sizesFromApi = parseList(
        p?.size ?? p?.sizes ?? p?.attributes?.size ?? null,
      );
      setCartItems((items) =>
        items.map((i) =>
          i.id === item.id
            ? {
                ...i,
                availableColors: colorsFromApi,
                availableSizes: sizesFromApi,
              }
            : i,
        ),
      );
      setEditingItem((prev) =>
        prev?.id === item.id
          ? {
              ...prev,
              availableColors: colorsFromApi,
              availableSizes: sizesFromApi,
            }
          : prev,
      );
    } catch {
      toast.error("Could not load product options.");
    } finally {
      setOptionsLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    setUpdating(true);
    try {
      await updateCartItem({
        lineId: editingItem.id,
        color: editColor,
        size: editSize,
      });
      setCartItems((items) =>
        items.map((i) =>
          i.id === editingItem.id
            ? { ...i, color: editColor, size: editSize }
            : i,
        ),
      );
      toast.success("Item updated!");
      setEditingItem(null);
    } catch {
      toast.error("Failed to update item.");
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditColor("");
    setEditSize("");
  };

  /* ── Delete selected ── */
  const handleDeleteSelected = async () => {
    const lineIds = cartItems.filter((i) => i.selected).map((i) => i.id);
    if (lineIds.length === 0) return;
    try {
      await removeSelected({ lineIds });
      setCartItems((items) => items.filter((i) => !i.selected));
      toast.success("Selected items removed.");
      updateCartCount();
    } catch {
      // fan-out fallback
      try {
        await Promise.all(lineIds.map((id) => removeCartItem({ lineId: id })));
        setCartItems((items) => items.filter((i) => !i.selected));
        toast.success("Selected items removed.");
        updateCartCount();
      } catch {
        toast.error("Failed to remove selected items.");
      }
    }
  };

  /* ── Checkout ── */
  const handleCheckout = () => {
    if (totalItems === 0) return;

    const authMode = getAuthMode();

    // For guests, item.id is already the Redis UUID from mapLines.
    // We explicitly expose it as cart_item_id so CheckoutPage can
    // build the ?cart_item_ids[]= query param for /guest/cart/checkout.
    const itemsForCheckout = selectedItems.map((item) => ({
      ...item,
      cart_item_id: item.id, // UUID for guest, numeric id for user — CheckoutPage handles both
    }));

    navigate("/checkout", {
      state: {
        items: itemsForCheckout,
        mode: authMode,
      },
    });
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="cart-screen">
        <div className="cart-container">
          <div
            className="cart-content"
            style={{
              padding: "60px",
              textAlign: "center",
              color: "var(--ct-muted)",
              fontSize: "14px",
            }}
          >
            Loading your cart…
          </div>
        </div>
      </div>
    );
  }

  const isEmpty = cartItems.length === 0;

  return (
    <div>
      <div className="cart-screen">
        <div className="cart-container">
          {/* ══════════════════════════
              LEFT: Cart items
          ══════════════════════════ */}
          <div className="cart-content">
            {/* Toolbar */}
            <div className="cart-header">
              <div className="cart-header-left">
                {!isEmpty && (
                  <label className="checkbox-container">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                    />
                    <span className="checkmark" />
                    Select All
                  </label>
                )}
                <span className="cart-item-count">
                  {cartItems.length} {cartItems.length === 1 ? "item" : "items"}
                </span>
              </div>

              {!isEmpty && (
                <button
                  className="delete-btn"
                  onClick={handleDeleteSelected}
                  type="button"
                >
                  <TrashIcon /> Delete
                </button>
              )}
            </div>

            {/* Empty */}
            {isEmpty ? (
              <div className="cart-empty">
                <div className="cart-empty-icon">
                  <BagIcon />
                </div>
                <p>Your cart is empty</p>
                <span>Add items to get started</span>
                <button
                  className="checkout-btn"
                  onClick={() => navigate("/")}
                  type="button"
                >
                  Start Shopping
                </button>
              </div>
            ) : (
              <div className="cart-items">
                {Object.entries(groupedItems)
                  .sort(([, a], [, b]) => {
                    // numeric ids: sort descending; uuid strings: preserve insertion order
                    if (
                      typeof a.latestId === "number" &&
                      typeof b.latestId === "number"
                    )
                      return b.latestId - a.latestId;
                    return 0;
                  })
                  .map(([vendorKey, store]) => {
                    const allStoreSelected = store.items.every(
                      (i) => i.selected,
                    );
                    return (
                      <div key={vendorKey} className="store-section">
                        {/* Store header */}
                        <div className="store-header">
                          <label className="checkbox-container store-checkbox">
                            <input
                              type="checkbox"
                              checked={allStoreSelected}
                              onChange={() => handleStoreSelect(vendorKey)}
                            />
                            <span className="checkmark" />
                            <span className="store-name-only">
                              {store.unique_name}
                            </span>
                          </label>
                          {store.unique_name && (
                            <button
                              className="visit-store-btn"
                              type="button"
                              onClick={() =>
                                navigate(`/store/${store.unique_name}`)
                              }
                            >
                              Visit Store →
                            </button>
                          )}
                        </div>

                        {/* Items */}
                        {store.items.map((item) => (
                          <div key={item.id} className="cart-item">
                            <label className="checkbox-container item-checkbox">
                              <input
                                type="checkbox"
                                checked={item.selected}
                                onChange={() => handleItemSelect(item.id)}
                              />
                              <span className="checkmark" />
                            </label>

                            <div className="item-image">
                              <img
                                src={item.image || ""}
                                alt={item.name}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            </div>

                            <div className="item-details">
                              <h3 className="item-name" title={item.name}>
                                {item.name}
                              </h3>
                              <div className="item-attributes">
                                {item.brand && (
                                  <span className="item-attr-tag">
                                    {item.brand}
                                  </span>
                                )}
                                {item.color && (
                                  <span className="item-attr-tag">
                                    🎨 {item.color}
                                  </span>
                                )}
                                {item.size && (
                                  <span className="item-attr-tag">
                                    📐 {item.size}
                                  </span>
                                )}
                                <button
                                  type="button"
                                  className="edit-attributes-btn"
                                  onClick={() => handleEditItem(item)}
                                  title="Edit options"
                                >
                                  <EditIcon /> Edit
                                </button>
                              </div>
                              <div className="item-price">
                                {item.isFlashSale && (
                                  <span className="ct-flash-badge">
                                    ⚡ Flash
                                  </span>
                                )}
                                <span
                                  className={
                                    item.isFlashSale ? "ct-price-flash" : ""
                                  }
                                >
                                  $
                                  {Number(
                                    item.subtotal ??
                                      item.unitPrice * item.quantity,
                                  ).toFixed(2)}
                                </span>
                                {item.originalPrice > item.unitPrice &&
                                  item.originalPrice > 0 && (
                                    <span className="ct-price-original">
                                      $
                                      {Number(
                                        item.originalPrice * item.quantity,
                                      ).toFixed(2)}
                                    </span>
                                  )}
                              </div>
                            </div>

                            <div className="quantity-controls">
                              <button
                                type="button"
                                className="quantity-btn minus"
                                onClick={() =>
                                  handleQuantityChange(item.id, -1)
                                }
                                disabled={item.quantity <= 1}
                              >
                                <MinusIcon />
                              </button>
                              <span className="quantity">{item.quantity}</span>
                              <button
                                type="button"
                                className="quantity-btn plus"
                                onClick={() => handleQuantityChange(item.id, 1)}
                              >
                                <PlusIcon />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {/* ══════════════════════════
              RIGHT: Order Summary
          ══════════════════════════ */}
          <div className="order-summary">
            <div className="order-summary-header">
              <ReceiptIcon />
              <span className="order-summary-header-title">Order Summary</span>
            </div>
            <div className="order-details">
              <div className="order-row">
                <span className="order-label">Selected Items</span>
                <span className="order-value">{totalItems}</span>
              </div>
              <div className="order-row total-row">
                <span className="order-label">Total Amount</span>
                <span className="order-value total-amount">
                  ${totalAmount.toFixed(2)}
                </span>
              </div>
              <button
                className="checkout-btn"
                type="button"
                onClick={handleCheckout}
                disabled={totalItems === 0}
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════
          Edit Options Modal
      ══════════════════════════ */}
      {editingItem && (
        <div className="review-modal-overlay" onClick={handleCancelEdit}>
          <div
            className="review-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="review-modal-header">
              <h3>Edit Item Options</h3>
              <button
                className="review-modal-close"
                type="button"
                onClick={handleCancelEdit}
              >
                ×
              </button>
            </div>

            <div className="review-modal-body">
              {/* Product preview */}
              <div className="modal-product-preview">
                <img
                  src={editingItem.image || ""}
                  alt={editingItem.name}
                  className="modal-product-img"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <div>
                  <p className="modal-product-name">{editingItem.name}</p>
                  <p className="modal-product-price">
                    ${Number(editingItem.unitPrice).toFixed(2)} / unit
                  </p>
                </div>
              </div>

              {optionsLoading && (
                <div className="options-loading-note">
                  <span className="ct-spinner" /> Loading available options…
                </div>
              )}

              {/* Color */}
              <div className="review-form-group">
                <label className="review-form-label">Color</label>
                {Array.isArray(editingItem.availableColors) &&
                editingItem.availableColors.length > 0 ? (
                  <div className="color-options">
                    {editingItem.availableColors.map((c) => (
                      <button
                        key={c}
                        type="button"
                        className={`color-option ${editColor === c ? "selected" : ""}`}
                        onClick={() => setEditColor(c)}
                        disabled={updating}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                ) : (
                  <input
                    type="text"
                    className="review-form-textarea"
                    placeholder="e.g. Red, Blue"
                    value={editColor}
                    onChange={(e) => setEditColor(e.target.value)}
                    disabled={updating}
                  />
                )}
              </div>

              {/* Size */}
              <div className="review-form-group">
                <label className="review-form-label">Size</label>
                {Array.isArray(editingItem.availableSizes) &&
                editingItem.availableSizes.length > 0 ? (
                  <div className="size-options">
                    {editingItem.availableSizes.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={`size-option ${editSize === s ? "selected" : ""}`}
                        onClick={() => setEditSize(s)}
                        disabled={updating}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                ) : (
                  <input
                    type="text"
                    className="review-form-textarea"
                    placeholder="e.g. M, L, XL"
                    value={editSize}
                    onChange={(e) => setEditSize(e.target.value)}
                    disabled={updating}
                  />
                )}
              </div>
            </div>

            <div className="review-modal-footer">
              <button
                className="review-cancel-btn"
                type="button"
                onClick={handleCancelEdit}
                disabled={updating}
              >
                Cancel
              </button>
              <button
                className="review-submit-btn"
                type="button"
                onClick={handleSaveEdit}
                disabled={updating}
              >
                {updating ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CartScreen;
